import { Phone, MessageCircle, Mail, Clock, MapPin } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { SITE, telLink, whatsappLink, mailLink } from "@/config/site";
import { track } from "@/lib/analytics";

const Row = ({ icon: Icon, label, value, href, onClick, testid, external }) => (
  <a
    href={href} onClick={onClick} data-testid={testid}
    target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined}
    className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md"
  >
    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><Icon size={20} /></span>
    <span>
      <span className="block text-xs font-bold uppercase tracking-wide text-slate-400">{label}</span>
      <span className="block font-bold text-slate-900">{value}</span>
    </span>
  </a>
);

export const Contact = () => (
  <section id="contact" className="bg-slate-50 py-20 sm:py-28" data-testid="contact-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid gap-12 lg:grid-cols-2">
        <Reveal>
          <p className="text-sm font-bold uppercase tracking-widest text-emerald-700">Get in touch</p>
          <h2 className="mt-3 font-display text-4xl font-black tracking-tight text-slate-900 sm:text-5xl">Contact Us</h2>
          <p className="mt-4 text-lg text-slate-600">
            The fastest way to a quote is WhatsApp — send photos or a short walkthrough video and we'll reply with a clear price.
          </p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            <Row icon={Phone} label="Call us" value={SITE.phoneDisplay} href={telLink()} onClick={track.phoneClick} testid="contact-phone" />
            <Row icon={MessageCircle} label="WhatsApp" value="Message us" href={whatsappLink()} onClick={() => track.whatsappClick("contact")} testid="contact-whatsapp" external />
            <Row icon={Mail} label="Email" value={SITE.email} href={mailLink()} onClick={track.emailClick} testid="contact-email" />
            <div className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><MapPin size={20} /></span>
              <span>
                <span className="block text-xs font-bold uppercase tracking-wide text-slate-400">Service area</span>
                <span className="block font-bold text-slate-900">{SITE.serviceArea}</span>
              </span>
            </div>
          </div>
          <div className="mt-6 flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700"><Clock size={20} /></span>
            <span>
              <span className="block text-xs font-bold uppercase tracking-wide text-slate-400">Opening hours</span>
              <span className="block font-semibold text-slate-800">{SITE.hours.weekdays}</span>
              <span className="block font-semibold text-slate-800">{SITE.hours.sunday}</span>
            </span>
          </div>
          <p className="mt-4 text-sm text-slate-400">We operate as a service-area business and do not publish a residential address.</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="h-full overflow-hidden rounded-3xl border border-slate-200 shadow-sm" data-testid="contact-map">
            <iframe
              title="GoCleaning Dublin service area map"
              src="https://www.google.com/maps?q=Dublin,Ireland&z=10&output=embed"
              className="h-full min-h-[420px] w-full"
              loading="lazy" referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
